/**
 * DTO + enum cho Notification Center (React Portal) — phản chiếu các DTO/enum backend
 * trong `SDJ.CRM.Application/Notifications/Center` và `SDJ.CRM.Core.Shared/Notifications`.
 *
 * API ABP: `/api/services/app/NotificationCenter/{Method}` với envelope chuẩn `{ result, success }`.
 * Tham chiếu: Requirements 6.1, 6.3, 6.4 (notification-center).
 */

/** Kênh gửi thông báo (khớp enum backend `NotificationChannel`, R2.5). */
export enum NotificationChannel {
  InApp = 1,
  Email = 2,
  Push = 3,
  SMS = 4,
}

/** Mức ưu tiên (khớp enum backend `NotificationPriority`, R2.4). */
export enum NotificationPriority {
  Critical = 1,
  High = 2,
  Normal = 3,
  Low = 4,
}

/** Trạng thái đọc của message (khớp enum backend `NotificationReadStatus`). */
export enum NotificationReadStatus {
  Unread = 1,
  Read = 2,
}

/** Chế độ lưu trữ (khớp enum backend `PersistenceMode`, R2.7). */
export enum PersistenceMode {
  Persistent = 1,
  Ephemeral = 2,
}

/** Trạng thái gửi theo từng Channel (khớp enum backend `ChannelDeliveryStatus`). */
export enum ChannelDeliveryStatus {
  Pending = 1,
  Sent = 2,
  Failed = 3,
  Deferred = 4,
  Suppressed = 5,
}

/** Cấp cấu hình 3 cấp (khớp enum backend `SettingLevel`, R4.1). */
export enum SettingLevel {
  General = 1,
  Project = 2,
  User = 3,
}

/** Loại Trigger (khớp enum backend `TriggerType`, R1.2). */
export enum TriggerType {
  UserAction = 1,
  WorkflowTransition = 2,
  ScheduledJob = 3,
  DomainEvent = 4,
  SystemEvent = 5,
}

/**
 * Phân hệ nghiệp vụ nguồn (khớp enum backend `Module`). Để tránh phụ thuộc chặt vào toàn bộ
 * danh sách Module phía backend, giữ kiểu số ở tầng client.
 */
export type NotificationModule = number

/** Trạng thái gửi của một message trên một Channel cụ thể (R2.5/R2.6). */
export interface NotificationMessageChannelDto {
  channel: NotificationChannel
  status: ChannelDeliveryStatus
  sentTime?: string | null
  retryCount: number
}

/** DTO hiển thị một `NotificationMessage` trong Notification Center (R6.3/6.4). */
export interface NotificationMessageDto {
  id: string
  eventCode: string
  category: string
  priority: NotificationPriority
  module: NotificationModule
  actorUserId?: number | null
  recipientUserId: number
  readStatus: NotificationReadStatus
  /** Thời điểm tạo — dùng để sắp xếp giảm dần (R2.6/R6.3). */
  createdTime: string
  persistence: PersistenceMode
  deepLink?: string | null
  payloadJson?: string | null
  dedupKey?: string | null
  groupingKey?: string | null
  expiredTime?: string | null
  readTime?: string | null
  channels: NotificationMessageChannelDto[]
}

/**
 * Đầu vào truy vấn danh sách thông báo người dùng hiện tại (R6.3/6.4). Mỗi tiêu chí lọc `null`/
 * `undefined` = không lọc theo tiêu chí đó. Kế thừa phân trang ABP (`maxResultCount`/`skipCount`).
 */
export interface GetMyNotificationsInput {
  readStatus?: NotificationReadStatus
  module?: NotificationModule
  priority?: NotificationPriority
  maxResultCount?: number
  skipCount?: number
  sorting?: string
}

/** DTO của một `NotificationDefinition` đã đăng ký trong Registry (R1.7). */
export interface NotificationDefinitionDto {
  id: number
  eventCode: string
  module: NotificationModule
  triggerType: TriggerType
  triggerDescription: string
  category: string
  defaultPriority: NotificationPriority
  defaultChannelsCsv: string
  defaultRecipientGroup: string
  templateKey?: string | null
  sourceJobIdentifier: string
  isActive: boolean
}

/** Bộ lọc truy vấn Registry; mọi tiêu chí tùy chọn (AND khi tổ hợp) (R1.7/1.8). */
export interface NotificationDefinitionFilter {
  module?: NotificationModule
  triggerType?: TriggerType
  channel?: NotificationChannel
}

/** Một ô của ma trận cấu hình (EventCode × Channel) ở scope hiện tại (R4.2/R6.8). */
export interface NotificationSettingMatrixCellDto {
  eventCode: string
  channel: NotificationChannel
  enabled: boolean
  decidedBy: SettingLevel
  /** `true` ⇒ UI hiển thị ON và khóa toggle do Mandatory_Rule (R4.3/R4.4/R6.8). */
  mandatoryLocked: boolean
  hasExplicitValue: boolean
  explicitEnabled?: boolean | null
  explicitIsMandatory: boolean
}

/** Ma trận cấu hình (Event × Channel) cho một cấp/scope (R6.7/6.8). */
export interface NotificationSettingMatrixDto {
  level: SettingLevel
  projectId?: number | null
  userId?: number | null
  cells: NotificationSettingMatrixCellDto[]
}

/** Đầu vào lấy ma trận cấu hình ở một cấp/scope (R6.7/6.8). */
export interface GetSettingsInput {
  level: SettingLevel
  projectId?: number | null
  userId?: number | null
}

/** Đầu vào cập nhật một ô cấu hình ở một cấp/scope (R4.8/4.10/6.8). */
export interface UpdateNotificationSettingInput {
  level: SettingLevel
  projectId?: number | null
  userId?: number | null
  eventCode: string
  channel: NotificationChannel
  enabled: boolean
  isMandatory: boolean
}
